import { suppressLoaderOnce } from "api/loadingBridge";
import { fetchFilteredMedia, MediaFilterParams, MediaItem } from "api/mediaApi";
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

interface FeedContextType {
    feeds: MediaItem[];
    loadFeeds: (append?: boolean) => Promise<void>;
    hasMore: boolean;
    filters: Partial<MediaFilterParams>;
    setFilters: React.Dispatch<React.SetStateAction<Partial<MediaFilterParams>>>;
    categories: Record<string, string[]>;
    riskTypes: string[];
    locationTree: Record<string, any>;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    isLoadingFeeds: boolean;
}

const FeedContext = createContext<FeedContextType | undefined>(undefined);

export const FeedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [feeds, setFeeds] = useState<MediaItem[]>([]);
    const [filters, setFiltersState] = useState<Partial<MediaFilterParams>>({
        category: "BestBuy",
        subCategory: "Electronics",
        country: "Any",
        riskDimension: "Any",
    });
    const [categories, setCategories] = useState<Record<string, string[]>>({});
    const [riskTypes, setRiskTypes] = useState<string[]>([]);
    const [locationTree, setLocationTree] = useState<Record<string, any>>({});
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingFeeds, setIsLoadingFeeds] = useState(false);

    // ✅ Clean filter values by removing "Any"
    const getCleanFilters = useCallback(() => {
        return Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "Any"));
    }, [filters]);

    // ✅ Load feeds with append option
    const loadFeeds = useCallback(async (append = false) => {
        if (append) suppressLoaderOnce();
        setIsLoadingFeeds(true); // ✅ Start grid loader
        try {
            const data = await fetchFilteredMedia({ ...getCleanFilters(), page, perPage: 10 });
            if (data) {
                setCategories(data.categories);
                setRiskTypes(data.riskTypes);
                setLocationTree(data.locationTree);
                setFeeds(prev => append ? [...prev, ...data.media] : data.media);
                setHasMore(page < data.meta.last_page);
            }
        } finally {
            setIsLoadingFeeds(false); // ✅ End grid loader
        }
    }, [getCleanFilters, page]);

    // ✅ Reload when filters change (reset to page 1)
    useEffect(() => {
        setPage(1);
    }, [filters]);

    // ✅ When page changes, load feeds
    useEffect(() => {
        loadFeeds(page > 1);
    }, [page, loadFeeds]);

    // ✅ Improved setFilters method (to prevent race conditions)
    const setFilters = (value: React.SetStateAction<Partial<MediaFilterParams>>) => {
        setFiltersState(prev => {
            const next = typeof value === "function" ? (value as Function)(prev) : value;
            // Reset page to 1 when filters change
            setPage(1);
            return next;
        });
    };

    return (
        <FeedContext.Provider
            value={{
                feeds,
                loadFeeds,
                hasMore,
                filters,
                setFilters,
                categories,
                riskTypes,
                locationTree,
                page,
                setPage,
                isLoadingFeeds
            }}
        >
            {children}
        </FeedContext.Provider>
    );
};

export const useFeed = () => {
    const context = useContext(FeedContext);
    if (!context) throw new Error("useFeed must be used within a FeedProvider");
    return context;
};